import { AssessmentReport } from './report';

export type TOCType = 'quiz' | 'lesson' | 'video';

export enum ModuleLevel {
  beginner = 1,
  intermediate = 2,
  advanced = 3,
  intro = 4,
}

export enum ModuleType {
  trial = 1,
  purchase = 2,
  subscription = 3,
  free = 4,
}

export interface Module {
  id: string;
  title: string;
  description: string;
  level: ModuleLevel;
  syllabus: Syllabus[];
  quizzes: QuizMeta[];
  ownerId: string;
  rating: number;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
  stats: ModuleStats;
  price?: ModulePrice;
  subscriptionPrice?: ModulePrice;
  fullPaymentPrice?: ModulePrice;
  videos: Video[];
  modules?: Module[];
  plan: Plan | null;
  isLocked: boolean;
  order: number | null;
  planOrder: number | null;
  playgroundFiles: PlaygroundFile[];
  hasTrial: boolean;
  type: ModuleType;
  nextPaymentTime: string | null;
}

export interface PlaygroundFile {
  fileName: string;
  link: string;
}

export interface Plan {
  id: string;
  title: string;
  imageUrl: string | null;
  nextModuleId: string | null;
  certificateUrl: string | null;
}

export interface ModulePrice {
  priceId: string;
  amount: number;
  currency: string;
  region: string;
  taxAmount: number;
  taxPercentage: number;
}

export interface ModuleStats {
  quizTotal: number;
  quizCompleted: number;
  articlesTotal: number;
  articlesCompleted: number;
  lessonTotal: number;
  lessonCompleted: number;
  progress: number;
  completed: boolean;
  failed: boolean;
  videoCompleted: number;
  videoTotal: number;
  assessmentReport?: AssessmentReport;
}

export interface QuizMeta {
  id: string;
  title: string;
  order: number;
  completed: boolean;
  failed: boolean;
  score: number;
  locked: boolean;
}

export interface SyllabusStats {
  totalCount: number;
  completedCount: number;
  completed: boolean;
}

export interface SyllabusSection {
  title: string;
  completed: boolean;
}

export interface Syllabus {
  id: string;
  title: string;
  order: number;
  sections: SyllabusSection[];
  stats: SyllabusStats;
}

export interface Review {
  id: string;
  moduleId: string;
  rating: number;
  comment: string;
  name: string;
}

export interface TOC {
  id: string;
  title: string;
  type: TOCType;
  sections: string[] | TOC[];
}

export interface CodeSnippet {
  section: string;
  name: string;
  code: string;
}

export interface Video {
  id: string;
  title: string;
  description: string;
  order: number;
  url: string;
  preview: string;
  completed: boolean;
}

export interface ModuleState {
  modulesUpdater: number;
  modulesByPlan: Map<string, Module[]>;
  recommendations: Module[];
  plans: Module[];
  trialPaymentNeeded: boolean;
  trialPrice?: ModulePrice;

  module: Module | undefined;
  plan: string | undefined;

  toc: TOC[];
  isReviewLeft: boolean;
  overallProgress: number;

  activeVideoId?: string;
}

export interface ModuleActions {
  updateModules: () => void;
  setModulesByPlan: (modulesByPlan: Map<string, Module[]>) => void;
  updateModuleByPlan: (planId: string, module: Module) => void;
  setRecommendations: (recommendations: Module[]) => void;
  setPlans: (plans: Module[]) => void;
  setTrialPaymentNeeded: (trialPaymentNeeded: boolean) => void;
  setTrialPrice: (trialPrice?: ModulePrice) => void;

  setModule: (module?: Module) => void;
  setPlan: (plan?: string) => void;
  completeSections: (moduleId: string, lessonId: string, sectionsAmount: number) => void;
  completeQuiz: (moduleId: string, quizId: string, score: number) => void;
  failQuiz: (moduleId: string, quizId: string) => void;
  completeVideo: (moduleId: string, videoId: string) => void;

  setTOC: (toc: TOC[]) => void;
  setReviewLeft: (isReviewLeft: boolean) => void;
  setOverallProgress: (overallProgress: number) => void;

  setActiveVideoId: (activeVideoId?: string) => void;
}
